import { useCallback, useEffect, useRef, useState } from "react";

/**
 * 滚动到底部的配置选项
 */
interface ScrollOptions {
    /** 是否启用自动滚动（当依赖变化时） */
    enabled?: boolean;
    /** 滚动行为：'auto' 即时滚动，'smooth' 平滑滚动 */
    behavior?: ScrollBehavior;
    /** 判断是否在底部的阈值（距离底部多少像素内算在底部），默认 50 */
    threshold?: number;
}

/**
 * 自动滚动到底部的 Hook
 *
 * 特性：
 * - 使用 ResizeObserver 监听内容区域高度变化，高度增长时自动滚动到底部
 * - 如果用户手动向上滚动，暂停自动滚动
 * - 当用户滚动回底部时，恢复自动滚动
 *
 * @param deps 触发自动滚动的依赖数组（兼容旧接口，首次渲染和 deps 变化时触发一次滚动）
 * @param options 配置选项
 * @returns containerRef, scrollToBottom 方法, isAtBottom 状态
 *
 * @example
 * ```tsx
 * const { containerRef, scrollToBottom, isAtBottom } = useAutoScrollToBottom([messages], {
 *   enabled: !loading && messages.length > 0
 * });
 *
 * return <div ref={containerRef}>...</div>;
 * ```
 */
export const useAutoScrollToBottom = <T extends HTMLElement = HTMLDivElement>(
    deps: unknown[] = [],
    options: ScrollOptions = {}
) => {
    const { enabled = true, behavior = "auto", threshold = 50 } = options;

    // 使用 state 存储容器，确保 ref 变化时能触发 effect 重新绑定
    const [container, setContainer] = useState<T | null>(null);
    // 最后一次程序滚动的时间戳（用于区分用户滚动和程序滚动）
    const lastProgrammaticScrollTimeRef = useRef<number>(0);
    // 用户是否手动滚动离开底部（使用 ref 避免闭包问题）
    const userScrolledAwayRef = useRef(false);
    const [userScrolledAway, setUserScrolledAway] = useState(false);
    // 上一次观察到的 scrollHeight，用于判断内容是否增长
    const lastScrollHeightRef = useRef<number>(0);

    // 程序滚动后的保护时间（ms），在此期间内的 scroll 事件被认为是程序触发的
    const PROGRAMMATIC_SCROLL_PROTECTION_TIME = 200;

    /**
     * Callback ref - 当 DOM 元素挂载/卸载时被调用
     */
    const containerRef = useCallback((node: T | null) => {
        setContainer(node);
    }, []);

    /**
     * 检查是否在底部
     */
    const checkIsAtBottom = useCallback(
        (el: T | null) => {
            if (!el) return true;
            const { scrollTop, scrollHeight, clientHeight } = el;
            return scrollHeight - scrollTop - clientHeight <= threshold;
        },
        [threshold]
    );

    /**
     * 立即滚动到底部（同步设置 scrollTop，不使用 rAF）
     */
    const scrollToBottom = useCallback(
        (scrollBehavior?: ScrollBehavior) => {
            if (!container) return;

            const { scrollHeight, clientHeight } = container;

            // 检查容器尺寸的有效性
            if (scrollHeight <= 0 || clientHeight <= 0) {
                return;
            }

            const targetScrollTop = scrollHeight - clientHeight;
            if (targetScrollTop < 0) {
                return;
            }

            // 记录程序滚动时间
            lastProgrammaticScrollTimeRef.current = Date.now();

            if (scrollBehavior === "smooth" || behavior === "smooth") {
                container.scrollTo({
                    top: targetScrollTop,
                    behavior: "smooth",
                });
            } else {
                container.scrollTop = targetScrollTop;
            }
        },
        [container, behavior]
    );

    /**
     * ResizeObserver：监听容器内部内容高度变化
     * 当 scrollHeight 增长时，如果用户没有手动滚动离开，则自动滚动到底部
     */
    useEffect(() => {
        if (!container || !enabled) return;

        // 初始化 lastScrollHeight
        lastScrollHeightRef.current = container.scrollHeight;

        const ro = new ResizeObserver(() => {
            if (userScrolledAwayRef.current) return;

            const newScrollHeight = container.scrollHeight;
            const delta = newScrollHeight - lastScrollHeightRef.current;
            lastScrollHeightRef.current = newScrollHeight;

            // 只在内容高度增长时滚动（避免缩小时也触发）
            if (delta <= 0) return;

            // 直接同步设置 scrollTop，不通过 rAF
            const targetScrollTop = newScrollHeight - container.clientHeight;
            if (targetScrollTop > 0) {
                lastProgrammaticScrollTimeRef.current = Date.now();
                container.scrollTop = targetScrollTop;
            }
        });

        // 观察容器内所有子元素的尺寸变化
        // 对容器本身观察可以捕获 scrollHeight 变化
        ro.observe(container);
        // 同时观察容器内的直接子元素，确保内容变化被捕获
        for (const child of Array.from(container.children)) {
            ro.observe(child);
        }

        // 使用 MutationObserver 监听子元素的增删，动态添加/移除 ResizeObserver 观察
        const mo = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
                for (const node of Array.from(mutation.addedNodes)) {
                    if (node instanceof Element) {
                        ro.observe(node);
                    }
                }
            }
        });
        mo.observe(container, { childList: true });

        return () => {
            ro.disconnect();
            mo.disconnect();
        };
    }, [container, enabled]);

    /**
     * 处理用户滚轮事件（只有用户操作才会触发 wheel 事件）
     */
    useEffect(() => {
        if (!container) return;

        // 用户使用滚轮向上滚动时，暂停自动滚动
        const handleWheel = (e: WheelEvent) => {
            // deltaY < 0 表示向上滚动
            if (e.deltaY < 0) {
                if (!userScrolledAwayRef.current) {
                    console.log("[useAutoScrollToBottom] 用户向上滚动，暂停自动滚动");
                    userScrolledAwayRef.current = true;
                    setUserScrolledAway(true);
                }
            }
        };

        // 检测滚动到底部（恢复自动滚动）
        const handleScroll = () => {
            // 如果距离上次程序滚动时间很短，忽略
            const timeSinceLastProgrammaticScroll = Date.now() - lastProgrammaticScrollTimeRef.current;
            if (timeSinceLastProgrammaticScroll < PROGRAMMATIC_SCROLL_PROTECTION_TIME) {
                return;
            }

            const isAtBottom = checkIsAtBottom(container);

            if (isAtBottom && userScrolledAwayRef.current) {
                console.log("[useAutoScrollToBottom] 用户滚动到底部，恢复自动滚动");
                userScrolledAwayRef.current = false;
                setUserScrolledAway(false);
            }
        };

        container.addEventListener("wheel", handleWheel, { passive: true });
        container.addEventListener("scroll", handleScroll, { passive: true });

        return () => {
            container.removeEventListener("wheel", handleWheel);
            container.removeEventListener("scroll", handleScroll);
        };
    }, [container, checkIsAtBottom]);

    // 兼容旧接口：deps 变化时也触发一次滚动（处理消息列表整体替换等场景）
    useEffect(() => {
        if (!enabled) return;
        if (userScrolledAwayRef.current) return;
        scrollToBottom();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [...deps, enabled, scrollToBottom]);

    /**
     * 重置滚动状态（强制恢复自动滚动）
     */
    const resetScroll = useCallback(() => {
        userScrolledAwayRef.current = false;
        setUserScrolledAway(false);
        // 重置后立即刷新 lastScrollHeight，避免误触发
        if (container) {
            lastScrollHeightRef.current = container.scrollHeight;
        }
    }, [container]);

    return {
        containerRef,
        scrollToBottom,
        isAtBottom: !userScrolledAway,
        resetScroll,
    };
};

export default useAutoScrollToBottom;
